import { computed, ref } from "vue"
import type { Client } from "@/entities/client"
import type { CreateBookingInput } from "@/entities/booking"
import { clampToRange } from "./rescheduleDays"
import { useRecordsStore } from "./store"

export function useCreateBookingForm() {
  const store = useRecordsStore()

  const selectedClient = ref<Client | null>(null)
  const clientName = ref("")
  const clientPhone = ref("")
  const clientId = ref<string | null>(null)
  const selectedServiceId = ref("")
  const selectedSlot = ref<string | null>(null)
  const localDate = ref(clampToRange(store.selectedDate, store.salonTz))
  const nearestHint = ref<{ tappedMin: number; chosenMin: number } | null>(null)

  const step = computed<"client" | "service" | "time">(() => {
    if (!selectedClient.value && !clientName.value.trim()) return "client"
    if (!selectedServiceId.value) return "service"
    return "time"
  })

  const canSubmit = computed(
    () =>
      (!!clientId.value || clientName.value.trim().length > 0) &&
      !!selectedServiceId.value &&
      selectedSlot.value !== null,
  )

  function pickClient(c: Client): void {
    selectedClient.value = c
    clientName.value = [c.firstName, c.lastName].filter(Boolean).join(" ")
    clientPhone.value = c.phone
    clientId.value = c.id
  }

  function applyInlineCreated(c: Client): void {
    pickClient(c)
  }

  function clearClient(): void {
    selectedClient.value = null
    clientName.value = ""
    clientPhone.value = ""
    clientId.value = null
    selectedServiceId.value = ""
    selectedSlot.value = null
    nearestHint.value = null
  }

  function buildInput(): CreateBookingInput {
    return {
      masterId: store.activeMasterId,
      serviceId: selectedServiceId.value,
      clientName: clientName.value.trim(),
      clientPhone: clientPhone.value.trim(),
      startsAt: selectedSlot.value!,
      ...(clientId.value ? { clientId: clientId.value } : {}),
    }
  }

  function reset(): void {
    selectedClient.value = null
    clientName.value = ""
    clientPhone.value = ""
    clientId.value = null
    selectedServiceId.value = ""
    selectedSlot.value = null
    localDate.value = clampToRange(store.selectedDate, store.salonTz)
    nearestHint.value = null
  }

  return {
    selectedClient,
    clientName,
    clientPhone,
    clientId,
    selectedServiceId,
    selectedSlot,
    localDate,
    nearestHint,
    step,
    canSubmit,
    pickClient,
    applyInlineCreated,
    clearClient,
    buildInput,
    reset,
  }
}
